import data from "../../shared/sharedData";

export default {
  modalPrefill: {
    type: "crop",
    machine_name: "eta_790a_addendum_a",
    modal_name: "addProfilesModal",
    title: "Crop/Agricultural Activity Profiles",
    description:
      "Select the Crop/Agricultural Activity profile(s) to populate in the Addendum A table.",
    isVisible: {
      when: "eta_790a_a9_is_addendum_a_completed",
      is: "YES",
    },
  },
  name: "Addendum A - Crops and/or Agricultural Activities",
  pageInfo: {
    text: `<strong>For Disclosure of Additional Temporary Agricultural Services and Wage Offer Information (Section A, Item 9)</strong>
    <br>
    <br>
    This Addendum must be fully completed and submitted with the Form ETA-790A when the employer needs to disclose additional information about the payment of wages for specific crops and/or agricultural activities and, if applicable, distinct work task(s) performed within that crop or agricultural activity.  The employer must disclose wage information covering all the crops or agricultural activities (and, if applicable, distinct work task(s) performed within that crop or agricultural activity) that workers will need to perform under the agricultural clearance order.  For example, where an employer has multiple applicable hourly wage rate offers (e.g., due to work being performed in different States subject to different hourly AEWRs), the employer must disclose the distinct hourly wage rate offer covering each crop or agricultural activity and State on the Addendum A.  Where one or more geographic areas within a State or territory have different applicable wage rates (e.g., due to work performed in different geographic areas within a State subject to different prevailing piece rates), the employer must disclose the distinct piece wage rate offer covering each crop activity or agricultural and state or territory on each row of the Addendum A <u>and use Column 5 to disclose the sub-state geographic area in which the work will be performed.</u>
    <br>
    <br>
    Addendum A will collect up to 5 rows of wage offer information for specific crops and/or agricultural activities and State or territory where work will be performed.  For electronic filings, if the employer needs to disclose more than 5 rows of wage offer information, the filing system will automatically provide the employer with the option of adding more rows to the Addendum A until the response is completed.  For mailed or paper filings, the employer will make one or more copies of the Addendum A to complete and attach to the Form ETA-790A.
    `,
    coloring: "grey",
    read_more: true,
    character_limit: 1133,
    isVisible: {
      when: "eta_790a_a9_is_addendum_a_completed",
      is: "YES",
    },
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify in Field A.9 whether additional wage rates may apply to the job duties identified under this job order.",
        type: "info",
        isVisible: {
          when: "eta_790a_a9_is_addendum_a_completed",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you indicated that no additional wage rates are applicable (per Field A.9), no Addendum A is necessary for this application.",
        type: "success",
        isVisible: {
          when: "eta_790a_a9_is_addendum_a_completed",
          is: "NO",
        },
      },
    ],
  },
  sections: [
    {
      code: "Addendum A",
      machine_name: "eta_790a_addendum_a",
      name: "Crops and/or Agricultural Activities",
      isModal: true,
      isVisible: {
        when: "eta_790a_a9_is_addendum_a_completed",
        is: "YES",
      },
      openModalHeader: "Add Addendum A",
      modalHeader: "Add Crop/Agricultural Activity",
      label:
        "Use the table below to enter the details about each crop, when applicable.",
      listType: "table",
      validate: ["tableRequired", "tableIncomplete"],
      showPagination: true,
      removeFromFieldArray: true,
      master_fieldArray: "eta_790a_addendum_a", // need this for inlineModal onEditInsertSelectedValueIntoModal for loading data
      keepSortOnModalEdit: true,
      isLinkage: true,
      modalFields: [
        {
          machine_name: "crop_id",
          label: "System-Generated Crop ID",
          widget: "table_linkage",
          linkages: {
            type: "createLinkageId",
            fromFieldArray: "eta_790a_addendum_a",
            toFieldArray: "eta_790a_addendum_b_employment",
            toLinkageIdFieldName: "crop_ids",
            linkageIdPrefix: "A",
            removeRow: false, // removes whole row vs removing id
            removalTableColumn: "Removal790AColumns",
          },
        },
        {
          machine_name: "addmaCropState",
          label: "Work State",
          validate: ["required"],
          widget: "select",
          selection: data.abbreviatedState,
          maxLength: 2,
          tooltip: `Enter the two-letter abbreviation for the State or territory in which the crop or agricultural activity will be performed.  For example, if the crop or agricultural activity will be performed in the State of California, enter “CA” in this field.`,
        },
        {
          machine_name: "crop_name",
          label: "Crop or Agricultural Activity",
          widget: "text",
          maxLength: 100,
          validate: ["required"],
          tooltip: `Enter the name of the crop or agricultural activity and, if applicable, distinct work task(s) performed within that crop or agricultural activity (e.g., Apple Harvesting – Gala; Custom Harvesting – All Grains; Citrus Harvesting – Valentia for Fresh Pick; Citrus Harvesting - Valentia for Juice).`,
        },
        {
          machine_name: "wage_offer",
          label: "Wage Offer",
          widget: "wage",
          maxLength: 4,
          validate: ["required"],
          tooltip: `Enter the wage offer for the crop or agricultural activity and, if applicable, distinct work task(s) performed within that crop or agricultural activity identified by the “Crop or Agricultural Activity” under Column 2 in currency format (e.g., $9.99).`,
        },
        {
          label: "Per",
          isGroup: {
            machine_name: "wage_per",
            label: "Per",
            required: true,
            validate: ["required"],
            tooltip: `Select only one box to identify whether the wage offer will be paid to workers by the <strong>HOUR</strong> or <strong>MONTH</strong>.`,
            fields: [
              {
                widget: "radio",
                choice: "Hour",
                value: "Hour",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Month",
                value: "Month",
                validate: ["required"],
              },
            ],
          },
        },
        {
          machine_name: "addmaPieceRate",
          label: "Piece Rate Offer",
          widget: "wage",
          validate: [
            {
              valueChecker: "addmaPieceRate",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "additional_info",
                check: "ifValueExists",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          tooltip: `Enter any piece rate that will be offered, advertised, and paid to workers performing the job duties or services in the “Crop or Agricultural Activity” under Column 2.`,
          maxLength: 4, // accounts for the "$" and decimals
        },
        {
          hideOnTable: true,
          machine_name: "additional_info",
          label:
            "Piece Rate Units / Estimated Hourly Rate / Special Pay Information",
          widget: "text_area",
          rows: 8,
          inputLimit: 500,
          validate: [
            { minLength: 5 },
            {
              valueChecker: "additional_info",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "addmaPieceRate",
                check: "ifValueExists",
                operator: ">",
                value: "0",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          tooltip:
            "Enter the piece rate units (e.g., tree size/spacing, weight/size/number of boxes picked/packed, dimensions of bags or boxes filled) and the estimated hourly wage rate equivalent for each piece rate or base rate that is offered in this row.  If applicable, enter additional information and/or any other special pay information (e.g., performance bonuses or incentives) associated with performing the job duties or services in the crop or agricultural activity and, if applicable, distinct work task(s) performed within that crop or agricultural activity covering the geographic area (e.g., State or territory).",
        },
      ],
    },
  ],
};
